import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t } from '../i18n.js';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';
import { route } from 'preact-router';
import Card from '../components/shared/Card.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { GOAL_STATUS } from './GoalsView.jsx';
import { fetchFeatures } from '../utils/features.js';
import { whenModule } from '../utils/plan.js';
import NewsAudienceLabel from '../components/shared/NewsAudienceLabel.jsx';
import DepartmentLabels from '../components/shared/DepartmentLabel.jsx';
import { setup, loadSetup, changeSetup, routeShown } from '../state/setup.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return t('dashboard.greeting.morning');
  if (h < 18) return t('dashboard.greeting.afternoon');
  return t('dashboard.greeting.evening');
}

function isOverdue(deadline) {
  return deadline && new Date(deadline) < new Date();
}

function formatDeadline(deadline) {
  if (!deadline) return '';
  const d = parseServerDate(deadline);
  const today = new Date();
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return parseServerDate(deadline).toLocaleDateString('nl-NL');
  if (diff === 0) return t('dashboard.dueToday');
  if (diff === 1) return t('dashboard.dueTomorrow');
  return parseServerDate(deadline).toLocaleDateString('nl-NL');
}

function formatEventDate(dateStr, timeStr) {
  const d = parseServerDate(dateStr);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const eventDay = parseServerDate(dateStr); eventDay.setHours(0,0,0,0);

  let dayLabel;
  if (eventDay.getTime() === today.getTime()) dayLabel = 'Vandaag';
  else if (eventDay.getTime() === tomorrow.getTime()) dayLabel = 'Morgen';
  else dayLabel = d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });

  return timeStr ? `${dayLabel} · ${timeStr}` : dayLabel;
}

// ── Club Hero ─────────────────────────────────────────────────────────────────

function ClubHero({ theme, stats, canManage }) {
  return (
    <div style={`border-radius:16px;overflow:hidden;margin-bottom:24px;position:relative;background:var(--color-primary)`}>
      {/* Banner image */}
      {theme.banner && (
        <img
          src={theme.banner}
          alt=""
          style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.25"
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}

      <div style="position:relative;padding:24px 28px;display:flex;align-items:center;gap:20px;flex-wrap:wrap">
        {/* Club logo / initial */}
        <div style="width:56px;height:56px;border-radius:12px;overflow:hidden;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;flex-shrink:0">
          {theme.logo ? (
            <img src={theme.logo} alt={theme.name} style="width:100%;height:100%;object-fit:contain" />
          ) : (
            <span style="font-size:24px;font-weight:800;color:#fff">{(theme.name || 'C')[0]}</span>
          )}
        </div>

        {/* Club name + subtitle */}
        <div style="flex:1;min-width:0">
          <h2 style="font-size:20px;font-weight:800;color:#fff;margin:0 0 4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            {theme.name || 'Mijn club'}
          </h2>
          <p style="font-size:13px;color:rgba(255,255,255,0.75);margin:0">
            Welkom terug op het clubportaal
          </p>
        </div>

        {/* Quick stats (for admins) */}
        {canManage && stats && (
          <div style="display:flex;gap:16px;flex-wrap:wrap">
            {[
              { val: stats.memberCount ?? 0, label: 'Leden', icon: '👥' },
              { val: stats.activeProcessCount ?? 0, label: 'Actieve processen', icon: '⚙️' },
              { val: stats.openTaskCount ?? 0, label: 'Open taken', icon: '✅' },
            ].map(s => (
              <div key={s.label} style="text-align:center;background:rgba(255,255,255,0.15);border-radius:10px;padding:10px 16px;min-width:70px">
                <div style="font-size:11px;margin-bottom:2px">{s.icon}</div>
                <div style="font-size:20px;font-weight:700;color:#fff;line-height:1">{s.val}</div>
                <div style="font-size:10px;color:rgba(255,255,255,0.7);margin-top:2px">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── News Banner ───────────────────────────────────────────────────────────────

function NewsBanner({ items }) {
  const [idx, setIdx] = useState(0);
  if (!items || items.length === 0) return null;
  const item = items[idx];

  return (
    <div style="margin-bottom:20px">
      <div style="font-size:13px;font-weight:600;color:var(--color-text-muted);margin-bottom:8px">📰 Nieuws</div>
      <div style={`border-radius:12px;overflow:hidden;background:var(--color-surface);border:1px solid var(--color-border);${item.is_pinned ? 'border-left:4px solid var(--color-primary)' : ''}`}>
        {item.image_url && (
          <div style="height:140px;overflow:hidden">
            <img src={item.image_url} alt="" style="width:100%;height:100%;object-fit:cover;display:block"
              onError={e => { e.target.parentElement.style.display = 'none'; }} />
          </div>
        )}
        <div style="padding:14px 18px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:5px;flex-wrap:wrap">
            {!!item.is_pinned && <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px;font-weight:500">📌 Vastgepind</span>}
            <DepartmentLabels targets={item.audience} />
            <NewsAudienceLabel audience={item.audience} />
            {item.from_complex && <span style="font-size:11px;background:#ecfdf5;color:#047857;padding:2px 8px;border-radius:10px;font-weight:500">🏟 {item.from_complex}</span>}
            <span style="font-size:12px;color:var(--color-text-muted)">{parseServerDate(item.published_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}</span>
          </div>
          <div style="font-weight:700;font-size:15px;margin-bottom:5px">{item.title}</div>
          <div style="font-size:13px;color:var(--color-text-muted);overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;line-height:1.5">{item.content}</div>
          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:10px">
            <button onClick={() => route('/news')} style="background:none;border:none;color:var(--color-primary);font-size:13px;font-weight:500;cursor:pointer;padding:0">Meer lezen →</button>
            {items.length > 1 && (
              <div style="display:flex;gap:6px;align-items:center">
                <button onClick={() => setIdx(i => (i - 1 + items.length) % items.length)} style="background:var(--color-border);border:none;border-radius:50%;width:22px;height:22px;cursor:pointer;font-size:12px">‹</button>
                <span style="font-size:12px;color:var(--color-text-muted)">{idx + 1}/{items.length}</span>
                <button onClick={() => setIdx(i => (i + 1) % items.length)} style="background:var(--color-border);border:none;border-radius:50%;width:22px;height:22px;cursor:pointer;font-size:12px">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Target Selector ───────────────────────────────────────────────────────────

function TargetSelector({ sections, selected, onChange }) {
  const [expanded, setExpanded] = useState({});

  function toggleSection(sectionName, categories) {
    const allSelected = categories.every(c => selected.includes(c));
    if (allSelected) {
      onChange(selected.filter(s => !categories.includes(s)));
    } else {
      const toAdd = categories.filter(c => !selected.includes(c));
      onChange([...selected, ...toAdd]);
    }
  }

  function toggleCategory(cat) {
    onChange(selected.includes(cat) ? selected.filter(s => s !== cat) : [...selected, cat]);
  }

  return (
    <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden">
      {/* All members option */}
      <label style="display:flex;align-items:center;gap:8px;padding:8px 12px;cursor:pointer;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border)">
        <input
          type="checkbox"
          checked={selected.length === 0}
          onChange={() => onChange([])}
          style="accent-color:var(--color-primary)"
        />
        <span style="font-size:13px;font-weight:500">👥 Alle leden</span>
      </label>

      {sections.map(sec => {
        const isExp = !!expanded[sec.id];
        const allChecked = sec.categories.length > 0 && sec.categories.every(c => selected.includes(c));
        const someChecked = sec.categories.some(c => selected.includes(c));

        return (
          <div key={sec.id} style="border-bottom:1px solid var(--color-border)">
            {/* Section header */}
            <div style="display:flex;align-items:center;gap:8px;padding:8px 12px">
              <input
                type="checkbox"
                checked={allChecked}
                ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                onChange={() => toggleSection(sec.name, sec.categories)}
                style="accent-color:var(--color-primary);flex-shrink:0"
              />
              <span style="font-size:13px;font-weight:600;flex:1;cursor:pointer" onClick={() => setExpanded(e => ({ ...e, [sec.id]: !e[sec.id] }))}>
                {sec.name}
              </span>
              {someChecked && (
                <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:1px 6px;border-radius:8px">{sec.categories.filter(c => selected.includes(c)).length}</span>
              )}
              <button
                onClick={() => setExpanded(e => ({ ...e, [sec.id]: !e[sec.id] }))}
                style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);padding:0;font-size:12px;line-height:1"
              >
                {isExp ? '▲' : '▼'}
              </button>
            </div>

            {/* Categories */}
            {isExp && sec.categories.length > 0 && (
              <div style="padding:0 12px 8px 32px;display:flex;flex-direction:column;gap:4px">
                {sec.categories.map(cat => (
                  <label key={cat} style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px">
                    <input
                      type="checkbox"
                      checked={selected.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      style="accent-color:var(--color-primary)"
                    />
                    {cat}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Event Form ────────────────────────────────────────────────────────────────

function EventForm({ initial, sections, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    event_date: initial?.event_date?.slice(0, 10) || '',
    event_time: initial?.event_time || '',
    location: initial?.location || '',
  });
  const [targets, setTargets] = useState(initial?.targets || []);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  return (
    <div style="margin-top:12px;display:flex;flex-direction:column;gap:10px">
      <input class="form-input" style="font-size:13px" placeholder="Naam evenement *" value={form.title} onInput={e => set('title', e.target.value)} />
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <input class="form-input" style="font-size:13px" type="date" value={form.event_date} onInput={e => set('event_date', e.target.value)} />
        <input class="form-input" style="font-size:13px" type="time" value={form.event_time} onInput={e => set('event_time', e.target.value)} />
      </div>
      <input class="form-input" style="font-size:13px" placeholder="Locatie (optioneel)" value={form.location} onInput={e => set('location', e.target.value)} />

      <div>
        <label style="display:block;font-size:11px;font-weight:500;color:var(--color-text-muted);margin-bottom:6px">Voor wie is dit evenement?</label>
        <TargetSelector sections={sections} selected={targets} onChange={setTargets} />
        {targets.length > 0 && (
          <div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px">
            {targets.map(t => (
              <span key={t} style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px">{t}</span>
            ))}
          </div>
        )}
      </div>

      <div style="display:flex;gap:6px">
        <Button variant="primary" size="sm" onClick={() => onSave({ ...form, targets })} disabled={saving || !form.title.trim() || !form.event_date}>
          {saving ? '...' : (initial ? 'Opslaan' : 'Toevoegen')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleren</Button>
      </div>
    </div>
  );
}

// ── Events Widget ─────────────────────────────────────────────────────────────

function EventsWidget({ events: initialEvents, canManage, onEventsChange }) {
  const [events, setEvents] = useState(initialEvents);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEvents(initialEvents); }, [initialEvents]);

  useEffect(() => {
    if (canManage) {
      api('/api/events/sections').then(data => setSections(Array.isArray(data) ? data : [])).catch(() => {});
    }
  }, [canManage]);

  async function handleSave(form) {
    setSaving(true);
    try {
      if (editEvent) {
        const updated = await api(`/api/events/${editEvent.id}`, { method: 'PUT', body: JSON.stringify(form) });
        const next = events.map(e => e.id === updated.id ? updated : e);
        setEvents(next);
        onEventsChange(next);
      } else {
        const created = await api('/api/events', { method: 'POST', body: JSON.stringify(form) });
        const next = [...events, created].sort((a, b) => a.event_date.localeCompare(b.event_date));
        setEvents(next);
        onEventsChange(next);
      }
      setShowForm(false);
      setEditEvent(null);
    } catch {}
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!confirm('Evenement verwijderen?')) return;
    try {
      await api(`/api/events/${id}`, { method: 'DELETE' });
      const next = events.filter(e => e.id !== id);
      setEvents(next);
      onEventsChange(next);
    } catch {}
  }

  function startEdit(ev) {
    setEditEvent(ev);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditEvent(null);
  }

  return (
    <Card title="📅 Agenda">
      {events.length === 0 && !showForm && (
        <p class="text-muted text-sm">Geen komende evenementen voor jouw secties.</p>
      )}

      {events.map(ev => (
        <div key={ev.id} style="display:flex;gap:12px;align-items:flex-start;padding:8px 0;border-bottom:1px solid var(--color-border)">
          {/* Date block */}
          <div style="text-align:center;background:var(--color-primary);color:#fff;border-radius:8px;padding:6px 10px;min-width:44px;flex-shrink:0">
            <div style="font-size:16px;font-weight:800;line-height:1">{new Date(ev.event_date).getDate()}</div>
            <div style="font-size:10px;text-transform:uppercase;opacity:0.85">
              {parseServerDate(ev.event_date).toLocaleDateString('nl-NL', { month: 'short' })}
            </div>
          </div>

          {/* Info */}
          <div style="flex:1;min-width:0">
            <div style="font-weight:500;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{ev.title}</div>
            <div style="font-size:12px;color:var(--color-text-muted)">{formatEventDate(ev.event_date, ev.event_time)}</div>
            {ev.location && <div style="font-size:12px;color:var(--color-text-muted)">📍 {ev.location}</div>}
            {ev.targets?.length > 0 && (
              <div style="display:flex;flex-wrap:wrap;gap:3px;margin-top:3px">
                {ev.targets.map(t => (
                  <span key={t} style="font-size:10px;background:var(--color-border);color:var(--color-text-muted);padding:1px 6px;border-radius:6px">{t}</span>
                ))}
              </div>
            )}
          </div>

          {/* Edit/delete (only for managers) */}
          {canManage && (
            <div style="display:flex;gap:4px;flex-shrink:0">
              <button
                onClick={() => startEdit(ev)}
                title="Bewerken"
                style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 7px;cursor:pointer;color:var(--color-text-muted);font-size:12px"
              >✏️</button>
              <button
                onClick={() => handleDelete(ev.id)}
                title="Verwijderen"
                style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 7px;cursor:pointer;color:var(--color-text-muted);font-size:12px"
              >🗑️</button>
            </div>
          )}
        </div>
      ))}

      {canManage && (
        showForm ? (
          <EventForm
            initial={editEvent}
            sections={sections}
            onSave={handleSave}
            onCancel={cancelForm}
            saving={saving}
          />
        ) : (
          <button
            onClick={() => { setEditEvent(null); setShowForm(true); }}
            style="margin-top:10px;background:none;border:1px dashed var(--color-border);border-radius:8px;width:100%;padding:8px;color:var(--color-text-muted);font-size:13px;cursor:pointer;text-align:center"
          >
            + Evenement toevoegen
          </button>
        )
      )}
    </Card>
  );
}

// ── Birthday Widget ───────────────────────────────────────────────────────────

function Avatar({ name, avatarUrl, size = 32 }) {
  if (avatarUrl) {
    return (
      <div style={`width:${size}px;height:${size}px;border-radius:50%;overflow:hidden;flex-shrink:0`}>
        <img src={avatarUrl} alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
      </div>
    );
  }
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const hue = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  return (
    <div style={`width:${size}px;height:${size}px;border-radius:50%;background:hsl(${hue},55%,55%);display:flex;align-items:center;justify-content:center;font-size:${size * 0.35}px;font-weight:700;color:#fff;flex-shrink:0`}>
      {initials}
    </div>
  );
}

function BirthdayRow({ member, label, highlight }) {
  return (
    <div style={`display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--color-border);${highlight ? 'background:var(--color-bg-secondary);margin:0 -8px;padding:7px 8px;border-radius:6px;border-bottom:none;' : ''}`}>
      <Avatar name={member.display_name} avatarUrl={member.avatar_url} size={30} />
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{member.display_name}</div>
      </div>
      <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;white-space:nowrap;${highlight ? 'background:#fef3c7;color:#92400e;border:1px solid #fde68a' : 'color:var(--color-text-muted)'}`}>
        {label}
      </span>
    </div>
  );
}

function BirthdayWidget({ birthdays }) {
  const { past, today, upcoming } = birthdays || { past: [], today: [], upcoming: [] };
  const total = past.length + today.length + upcoming.length;
  if (total === 0) return (
    <Card title="🎂 Verjaardagen">
      <p class="text-muted text-sm">Geen verjaardagen in de komende 10 dagen.</p>
    </Card>
  );
  return (
    <Card title="🎂 Verjaardagen">
      {today.map(m => <BirthdayRow key={m.id} member={m} label="🎉 Vandaag jarig!" highlight />)}
      {past.map(m => <BirthdayRow key={m.id} member={m} label={`${Math.abs(m.diff)}d geleden`} highlight={false} />)}
      {upcoming.map(m => <BirthdayRow key={m.id} member={m} label={m.diff === 1 ? 'Morgen' : `Over ${m.diff} dagen`} highlight={false} />)}
    </Card>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonLine({ width = '100%', height = '16px', style = '' }) {
  return <div class="skeleton" style={`width:${width};height:${height};${style}`} />;
}

function DashboardSkeleton() {
  return (
    <div>
      <SkeletonLine width="100%" height="108px" style="margin-bottom:24px;border-radius:16px" />
      <div class="grid-3 mb-6">
        {[0, 1, 2].map(i => (
          <div class="stat-card" key={i}>
            <SkeletonLine width="48px" height="36px" style="margin-bottom:8px" />
            <SkeletonLine width="120px" height="14px" />
          </div>
        ))}
      </div>
      <div class="grid-2 mt-4">
        {[0, 1].map(i => (
          <div class="card" key={i} style="padding:20px">
            <SkeletonLine width="140px" height="16px" style="margin-bottom:16px" />
            {[0, 1, 2].map(j => (
              <SkeletonLine key={j} height="44px" style="margin-bottom:8px;border-radius:8px" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// "Onze doelen": the club's vision in one line and the status of the goals per focus
// area. On the dashboard for roles with goals:read (as in the menu); hidden while
// nothing is set up.
function GoalsWidget({ featureKey }) {
  const [data, setData] = useState(null);
  const shown = useAuth().hasPermission('goals', 'read');
  useEffect(() => {
    // Not usable for this club or role (services/features.js, the plan): don't even ask
    if (shown) fetchFeatures(featureKey).then(f => f.goals && api('/api/goals').then(setData)).catch(() => {});
  }, [featureKey, shown]);
  if (!data || (!data.strategy?.vision && !data.focus_areas?.length)) return null;
  const active = a => a.goals.filter(g => !g.stopped_at);
  return (
    <Card title="🎯 Onze doelen">
      <div style="display:flex;flex-direction:column;gap:8px">
        {data.strategy.vision && (
          <div class="text-sm" style="font-style:italic;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden">{data.strategy.vision}</div>
        )}
        {data.focus_areas.map(a => (
          <div key={a.id} style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--color-border)">
            <span style="font-size:14px;font-weight:500;min-width:0">{a.title}</span>
            <span role="img" aria-label={active(a).map(g => `${g.title}: ${GOAL_STATUS[g.status]?.label || ''}`).join('; ') || 'Nog geen doelen'} style="display:flex;gap:3px;flex-shrink:0" title={`${active(a).length} ${active(a).length === 1 ? 'doel' : 'doelen'}`}>
              {active(a).map(g => <span key={g.id} title={`${g.title}: ${GOAL_STATUS[g.status]?.label || ''}`} style={`width:10px;height:10px;border-radius:50%;background:${(GOAL_STATUS[g.status] || GOAL_STATUS.op_schema).color}`} />)}
            </span>
          </div>
        ))}
        <a href="/goals" class="text-sm">Bekijk visie &amp; doelen →</a>
      </div>
    </Card>
  );
}

// "Clubtrots": the volunteer of the month and the latest pride moment. For roles with
// pride:read, while the feature is usable — nothing is fetched otherwise.
function PrideWidget({ featureKey }) {
  const [data, setData] = useState(null);
  const shown = useAuth().hasPermission('pride', 'read');
  useEffect(() => {
    if (shown) fetchFeatures(featureKey).then(f => f.pride && api('/api/pride').then(setData)).catch(() => {});
  }, [featureKey, shown]);
  if (!data || (!data.current && !data.moments?.length)) return null;
  const moment = data.moments[0];
  return (
    <Card title="🏆 Clubtrots">
      <div style="display:flex;flex-direction:column;gap:10px">
        {data.current && (
          <div style="display:flex;gap:10px;align-items:center">
            <Avatar name={data.current.display_name || '?'} avatarUrl={data.current.avatar_url} size={44} />
            <div style="min-width:0">
              <div class="text-sm text-muted">⭐ Vrijwilliger van {new Date(`${data.current.month}-15T12:00:00`).toLocaleDateString('nl-NL', { month: 'long' })}</div>
              <div style="font-weight:700">{data.current.display_name || 'Oud-lid'}</div>
              <div class="text-sm" style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">{data.current.reason}</div>
            </div>
          </div>
        )}
        {moment && <div class="text-sm">🎉 <strong>{moment.title}</strong></div>}
        <a href="/clubtrots" class="text-sm">Naar clubtrots →</a>
      </div>
    </Card>
  );
}

// Overdrachten this member received and hasn't confirmed yet
function HandoverBanner({ clubKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { whenModule(clubKey, 'handovers').then(on => on && api('/api/handovers/mine').then(setItems)).catch(() => {}); }, [clubKey]);
  if (!items.length) return null;
  return (
    <div class="alert mb-4" style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);display:flex;flex-direction:column;gap:4px">
      {items.map(o => (
        <div key={o.id} style="font-size:14px">
          🤝 <strong>{o.from_name || 'Een oud-lid'}</strong> {o.item_count ? `heeft ${o.item_count} onderde${o.item_count === 1 ? 'el' : 'len'} aan je overgedragen.` : 'heeft een overdracht met notities voor je klaargezet.'} <a href={`/overdracht/${o.id}`}>Bekijken en bevestigen →</a>
        </div>
      ))}
    </div>
  );
}

// Welkom: a new function or volunteer role not looked at yet, or a check-in to answer
function WelcomeBanner({ clubKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { whenModule(clubKey, 'welcome').then(on => on && api('/api/welcome/mine').then(setItems)).catch(() => {}); }, [clubKey]);
  if (!items.length) return null;
  return (
    <div class="alert mb-4" style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);display:flex;flex-direction:column;gap:4px">
      {items.map(w => (
        <div key={w.id} style="font-size:14px">
          {w.checkin_sent_at && w.seen_at
            ? <>💬 Hoe gaat het als <strong>{w.label}</strong>? <a href={`/welkom/${w.id}`}>Laat het weten →</a></>
            : <>👋 {w.kind === 'klus' ? <>Je helpt mee: <strong>{w.label}</strong>.</> : <>Welkom als <strong>{w.label}</strong>!</>} <a href={`/welkom/${w.id}`}>Bekijk wat je helpt om te beginnen →</a></>}
        </div>
      ))}
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

// "Help mee": open jobs and vacancies of the klussenbord, one click to sign up. Uses
// the public API (the member is recognised by their session), so no extra permission.
function HelpWidget({ slug, clubKey }) {
  const [data, setData] = useState(null);
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) return;
    whenModule(clubKey, 'volunteers').then(on => on && fetch(`/api/public/volunteers/${encodeURIComponent(slug)}`).then(r => (r.ok ? r.json() : null)).then(setData)).catch(() => {});
  }, [slug, clubKey]);
  if (!data?.me) return null;
  const mine = new Set(data.me.signed_up || []);
  const open = data.openings.filter(o => !mine.has(o.id) && o.spots_left !== 0).slice(0, 3);
  if (!open.length && !Object.keys(done).length) return null;

  async function help(o) {
    if (busy) return;
    setBusy(o.id);
    setError(null);
    try {
      const r = await fetch(`/api/public/volunteers/${encodeURIComponent(slug)}/openings/${o.id}/signups`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error);
      setDone(d => ({ ...d, [o.id]: true }));
    } catch (e) {
      setError({
        already_signed_up: 'Je had je hier al voor aangemeld.', full: 'Deze is net vol.',
        queue_full: 'Er zijn al veel aanmeldingen; probeer het later nog eens.', too_many_requests: 'Even te veel aanmeldingen; probeer het later nog eens.',
      }[e.message] || 'Aanmelden lukte niet.');
    }
    setBusy(null);
  }

  const when = o => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(o.starts_at || '');
    return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) + ` ${m[4]}` : o.commitment || 'Vaste rol';
  };
  return (
    <Card title="🙋 Help mee">
      <div style="display:flex;flex-direction:column;gap:8px">
        {open.map(o => (
          <div key={o.id} style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--color-border)">
            <div style="min-width:0">
              <div style="font-weight:500;font-size:14px">{o.title}</div>
              <div class="text-sm text-muted">{[when(o), o.location].filter(Boolean).join(' · ')}</div>
            </div>
            {done[o.id]
              ? <span class="text-sm" style="color:var(--color-success,#1e7b3c);white-space:nowrap">Aangemeld ✓</span>
              : <Button size="sm" disabled={busy === o.id} onClick={() => help(o)}>{o.kind === 'vacature' ? 'Interesse' : 'Ik help mee'}</Button>}
          </div>
        ))}
        {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
        <a href={`/${slug}/vrijwilligers?from=app`} target="_blank" rel="noopener" class="text-sm">Alle klussen en vacatures →</a>
      </div>
    </Card>
  );
}

// Zolang de club nog niet is ingericht: waar de inrichter staat en de knop om verder te gaan
function SetupCard() {
  const [error, setError] = useState('');
  // Altijd vers: een seizoen of structuur kan net elders zijn gemaakt
  useEffect(() => { loadSetup({ force: true }); }, []);
  const s = setup.value;
  if (!routeShown(s)) return null;
  const hide = () => changeSetup('/api/setup/visibility', { method: 'PUT', body: JSON.stringify({ hidden: true }) })
    .catch(() => setError('Verbergen lukte niet. Probeer het nog eens.'));
  const phase = s.phases.find(p => p.done < p.total) || s.phases[s.phases.length - 1];
  const phaseNo = s.phases.indexOf(phase) + 1;
  const pct = Math.round((s.done / s.total) * 100);
  return (
    <div class="card mb-6" style="padding:18px 20px;display:flex;align-items:center;gap:18px;flex-wrap:wrap;border-left:4px solid var(--color-primary)">
      <div style="font-size:28px">🧭</div>
      <div style="flex:1;min-width:220px">
        <div style="font-weight:700">Jullie club inrichten</div>
        <div class="text-sm text-muted">Fase {phaseNo} van {s.phases.length}: {phase.title} · {s.done} van {s.total} stappen klaar</div>
        <div style="height:6px;border-radius:3px;background:var(--color-bg-secondary);overflow:hidden;margin-top:8px;max-width:360px">
          <div style={`height:100%;width:${pct}%;background:var(--color-primary)`} />
        </div>
      </div>
      <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">
        <Button onClick={() => route('/inrichten')}>{s.done ? 'Verder met inrichten' : 'Start de inrichting'}</Button>
        <button onClick={hide} title="Weer tonen kan bij Instellingen" style="border:none;background:none;color:var(--color-text-muted);font-size:12px;cursor:pointer;padding:2px">Verbergen</button>
        {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
      </div>
    </div>
  );
}

// A sportcomplex has no club dashboard (app.jsx sends its staff to Storingen): don't
// load the club's data on the way there
export default function DashboardView() {
  return useAuth().isComplex ? null : <ClubDashboard />;
}

function ClubDashboard() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(false);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const { displayName } = authState.value;
  const auth = useAuth();
  const theme = useTheme();
  const [mySteps, setMySteps] = useState([]);
  const [birthdays, setBirthdays] = useState(null);

  const canManage = auth.hasPermission('settings', 'update');
  const canStartProcess = auth.hasPermission('processes', 'create');

  useEffect(() => {
    Promise.all([
      api('/api/dashboard'),
      api('/api/templates').catch(() => []),
      api('/api/processes/my-steps').catch(() => []),
      api('/api/news').catch(() => []),
      api('/api/events').catch(() => []),
      api('/api/profiles/birthdays').catch(() => ({ past: [], today: [], upcoming: [] })),
    ])
      .then(([dashboard, tpls, steps, newsItems, evItems, bdayItems]) => {
        setData(dashboard);
        setTemplates(Array.isArray(tpls) ? tpls.filter(t => t.is_active) : []);
        setMySteps(Array.isArray(steps) ? steps : []);
        setNews(Array.isArray(newsItems) ? newsItems : []);
        setEvents(Array.isArray(evItems) ? evItems : []);
        setBirthdays(bdayItems);
      })
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 0;gap:16px">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:var(--color-text-muted);opacity:0.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <p class="text-muted">{t('errors.generic')}</p>
        <Button variant="secondary" size="sm" onClick={() => { setError(false); setData(null); location.reload(); }}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  if (!data) return <DashboardSkeleton />;

  return (
    <div>
      {/* Greeting */}
      <h1 class="page-title mb-4">{getGreeting()}, {displayName}</h1>

      {/* Club Hero */}
      <ClubHero theme={theme} stats={data.clubStats} canManage={canManage} />

      {/* Inrichten: alleen voor wie de club inricht */}
      {canManage && !auth.isComplex && !auth.isPlatformAdmin && <SetupCard />}

      {/* News Banner */}
      <NewsBanner items={news} />

      {/* Overdracht to confirm */}
      <HandoverBanner clubKey={`${auth.userId}:${auth.activeClubId}`} />

      {/* Welkom */}
      <WelcomeBanner clubKey={`${auth.userId}:${auth.activeClubId}`} />

      {/* Personal stat cards */}
      <div class="grid-3 mb-6">
        <div class="stat-card" style="cursor:pointer" onClick={() => route('/tasks?ownerFilter=mine')}>
          <div class="stat-card-value">{data.myTasks?.length ?? 0}</div>
          <div class="stat-card-label">{t('dashboard.stats.openTasks')}</div>
        </div>
        <div class="stat-card" style="cursor:pointer" onClick={() => route('/tasks?ownerFilter=mine&statusFilter=open')}>
          <div class="stat-card-value">{data.deadlines?.length ?? 0}</div>
          <div class="stat-card-label">{t('dashboard.stats.deadlinesThisWeek')}</div>
        </div>
        <div class="stat-card" style="cursor:pointer" onClick={() => route('/processes?status=actief')}>
          <div class="stat-card-value">{data.activeProcesses?.length ?? 0}</div>
          <div class="stat-card-label">{t('dashboard.stats.activeProcessCount')}</div>
        </div>
      </div>

      {/* Main content grid */}
      <div class="grid-2 mt-4">
        {/* Upcoming deadlines */}
        {data.deadlines?.length > 0 && (
          <Card title={t('dashboard.upcomingDeadlines')}>
            {data.deadlines.map(task => (
              <div key={task.id} class="list-item" onClick={() => route(`/tasks/${task.id}`)}>
                <div style="flex:1;min-width:0">
                  <div style="font-weight:500;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{task.title}</div>
                  <div class="text-sm" style={isOverdue(task.deadline) ? 'color:var(--color-danger)' : 'color:var(--color-text-muted)'}>
                    {formatDeadline(task.deadline)}
                  </div>
                </div>
                <StatusBadge status={task.status} type="task" />
              </div>
            ))}
          </Card>
        )}

        {/* My active process steps */}
        {mySteps.length > 0 && (
          <Card title={t('dashboard.myActiveSteps')}>
            {mySteps.map(step => (
              <div key={step.id} class="list-item" style="cursor:pointer;align-items:flex-start" onClick={() => route(`/processes/${step.process_id}`)}>
                <div style="flex:1;min-width:0">
                  <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:2px;display:flex;align-items:center;gap:6px">
                    <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:var(--color-primary);flex-shrink:0" />
                    {step.process_title}
                  </div>
                  <div style="font-weight:500;font-size:14px">{step.title}</div>
                  {(step.responsible_role_name || step.on_behalf_of) && (
                    <div class="text-sm text-muted">{[step.on_behalf_of && `🌴 namens ${step.on_behalf_of}`, step.responsible_role_name].filter(Boolean).join(' · ')}</div>
                  )}
                </div>
                <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px;white-space:nowrap;flex-shrink:0">
                  {t('dashboard.active')}
                </span>
              </div>
            ))}
          </Card>
        )}

        {/* Agenda widget */}
        <EventsWidget
          events={events}
          canManage={canManage}
          onEventsChange={setEvents}
        />

        {/* Birthday widget */}
        <BirthdayWidget birthdays={birthdays} />

        {/* Clubtrots */}
        <PrideWidget featureKey={`${auth.userId}:${auth.activeClubId}`} />

        {/* Visie & doelen */}
        <GoalsWidget featureKey={`${auth.userId}:${auth.activeClubId}`} />

        {/* Klussenbord */}
        <HelpWidget slug={(auth.clubs || []).find(c => Number(c.id) === Number(auth.activeClubId))?.slug} clubKey={`${auth.userId}:${auth.activeClubId}`} />
      </div>
    </div>
  );
}
